import { useEffect, useRef, useState } from 'react';

const SIZE = 64;
const EDGE = 12;
const bounds = (point, width, height) => ({
  x: Math.max(EDGE, Math.min(point.x, width - SIZE - EDGE)),
  y: Math.max(EDGE, Math.min(point.y, height - SIZE - EDGE)),
});

export const useBubblePosition = (userId) => {
  const key = `dutilyne_assistant_position_${userId}`;
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [point, setPoint] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key));
      if (Number.isFinite(saved?.x) && Number.isFinite(saved?.y)) return bounds(saved, window.innerWidth, window.innerHeight);
    } catch { /* Use the usual bottom-right position. */ }
    return bounds({ x: window.innerWidth - SIZE - 20, y: window.innerHeight - SIZE - 148 }, window.innerWidth, window.innerHeight);
  });
  const position = useRef(point);
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const [dragging, setDragging] = useState(false);
  const move = (next, persist = false) => {
    const clamped = bounds(next, window.innerWidth, window.innerHeight);
    position.current = clamped;
    setPoint(clamped);
    if (persist) localStorage.setItem(key, JSON.stringify(clamped));
  };
  useEffect(() => {
    const resize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      const clamped = bounds(position.current, window.innerWidth, window.innerHeight);
      position.current = clamped;
      setPoint(clamped);
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const handlers = {
    onPointerDown: event => {
      if (event.button !== 0) return;
      suppressClick.current = false;
      drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, start: position.current };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: event => {
      if (!drag.current || event.pointerId !== drag.current.id) return;
      const dx = event.clientX - drag.current.x;
      const dy = event.clientY - drag.current.y;
      if (!suppressClick.current && Math.hypot(dx, dy) < 6) return;
      suppressClick.current = true;
      setDragging(true);
      move({ x: drag.current.start.x + dx, y: drag.current.start.y + dy });
    },
    onPointerUp: event => {
      if (!drag.current || event.pointerId !== drag.current.id) return;
      if (suppressClick.current) move(position.current, true);
      drag.current = null;
      setDragging(false);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel: () => { drag.current = null; setDragging(false); suppressClick.current = false; },
    onKeyDown: event => {
      const delta = { ArrowLeft: [-20, 0], ArrowRight: [20, 0], ArrowUp: [0, -20], ArrowDown: [0, 20] }[event.key];
      if (!delta) return;
      event.preventDefault();
      move({ x: position.current.x + delta[0], y: position.current.y + delta[1] }, true);
    },
  };
  const above = point.y - EDGE * 2;
  const below = viewport.height - point.y - SIZE - EDGE * 2;
  const height = Math.min(500, Math.max(above, below));
  const width = Math.min(360, viewport.width - EDGE * 2);
  const panelStyle = {
    width, height,
    left: Math.max(EDGE, Math.min(point.x + SIZE - width, viewport.width - width - EDGE)),
    top: above >= below ? Math.max(EDGE, point.y - height - EDGE) : point.y + SIZE + EDGE,
  };
  return { point, panelStyle, dragging, handlers, allowClick: () => {
    const allowed = !suppressClick.current;
    suppressClick.current = false;
    return allowed;
  } };
};